<template>
  <div class="container special-service-page">
    <div class="service-layout">
      <aside class="service-sidebar" aria-label="自助服务菜单">
        <ServiceLeft active-index="li_special_service" />
      </aside>

      <main class="service-content">
        <template v-if="!receipt">
          <h1 class="page-title">特殊服务申请</h1>
          <p class="page-lead">请填写旅客与航班信息，我们会尽快联系您确认服务安排。</p>
          <el-alert
            class="time-notice"
            title="请不晚于航班起飞前48小时提交申请"
            type="warning"
            :closable="false"
            show-icon
          />
          <el-form
            ref="applicationForm"
            :model="form"
            :rules="rules"
            :disabled="submitting"
            label-position="top"
            @submit.native.prevent="submitApplication"
          >
            <section class="form-section" aria-labelledby="passenger-title">
              <div class="section-heading">
                <h2 id="passenger-title"><span>01</span>旅客信息</h2>
                <p>用于联系与核对申请人</p>
              </div>
              <el-row :gutter="24">
                <el-col :span="12">
                  <el-form-item label="乘机人姓名" prop="passengerName">
                    <el-input id="passengerName" v-model.trim="form.passengerName" placeholder="请填写与购票时一致的乘机人姓名" autocomplete="name" :maxlength="100" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="联系电话" prop="mobile">
                    <el-input id="mobile" v-model.trim="form.mobile" placeholder="请输入联系电话" type="tel" autocomplete="tel" :maxlength="11" />
                  </el-form-item>
                </el-col>
              </el-row>
            </section>

            <section class="form-section" aria-labelledby="ticket-title">
              <div class="section-heading">
                <h2 id="ticket-title"><span>02</span>客票信息</h2>
                <p>用于核对航班与申请时限</p>
              </div>
              <el-row :gutter="24">
                <el-col :span="12">
                  <el-form-item label="票号" prop="ticketNumber">
                    <el-input id="ticketNumber" v-model.trim="form.ticketNumber" placeholder="请输入票号" autocomplete="off" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="航班号" prop="flightNumber">
                    <el-input id="flightNumber" v-model.trim="form.flightNumber" placeholder="例如 A6 1234" autocomplete="off" @blur="normalizeFlightNumber" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="航班日期" prop="flightDate">
                    <el-date-picker
                      id="flightDate"
                      v-model="form.flightDate"
                      type="date"
                      value-format="yyyy-MM-dd"
                      format="yyyy-MM-dd"
                      placeholder="请选择航班日期"
                      :editable="false"
                      :picker-options="datePickerOptions"
                    />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="出发机场" prop="origin">
                    <div class="airport-field" @click.stop>
                      <el-autocomplete
                        id="origin"
                        v-model="form.origin"
                        placeholder="请选择出发机场"
                        clearable
                        :fetch-suggestions="(query, callback) => searchAirports('origin', query, callback)"
                        @input="onAirportInput('origin')"
                        @focus="openAirportPanel('origin')"
                        @select="airport => selectAirport('origin', airport)"
                      >
                        <template slot-scope="{ item }">
                          <span>{{ item.chineseName }}</span><span class="airport-code">{{ item.airportCode }}</span>
                        </template>
                      </el-autocomplete>
                      <AirportPannel ref="originPanel" @selected="airport => selectAirport('origin', airport)" />
                    </div>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="到达机场" prop="destination">
                    <div class="airport-field" @click.stop>
                      <el-autocomplete
                        id="destination"
                        v-model="form.destination"
                        placeholder="请选择到达机场"
                        clearable
                        :fetch-suggestions="(query, callback) => searchAirports('destination', query, callback)"
                        @input="onAirportInput('destination')"
                        @focus="openAirportPanel('destination')"
                        @select="airport => selectAirport('destination', airport)"
                      >
                        <template slot-scope="{ item }">
                          <span>{{ item.chineseName }}</span><span class="airport-code">{{ item.airportCode }}</span>
                        </template>
                      </el-autocomplete>
                      <AirportPannel ref="destinationPanel" @selected="airport => selectAirport('destination', airport)" />
                    </div>
                  </el-form-item>
                </el-col>
              </el-row>
              <p class="ticket-notice"><i class="el-icon-info" aria-hidden="true"></i>请核对客票信息，申请时限以航班起飞时间为准。</p>
            </section>

            <section class="form-section" aria-labelledby="needs-title">
              <div class="section-heading">
                <h2 id="needs-title"><span>03</span>服务需求</h2>
                <p>每项请选择一个选项</p>
              </div>
              <el-form-item label="残疾类型" prop="disability">
                <el-radio-group v-model="form.disability" class="choice-grid disability-choices" aria-label="残疾类型">
                  <el-radio v-for="item in disabilityTypes" :key="item.value" :label="item.value" border>{{ item.label }}</el-radio>
                </el-radio-group>
              </el-form-item>
              <el-form-item label="特殊服务需求" prop="service">
                <el-radio-group v-model="form.service" class="choice-grid service-choices" aria-label="特殊服务需求">
                  <el-radio v-for="item in serviceTypes" :key="item.value" :label="item.value" border>{{ item.label }}</el-radio>
                </el-radio-group>
              </el-form-item>
              <el-form-item v-if="requiresOtherDescription" label="其他需求说明" prop="otherText">
                <el-input id="otherText" v-model.trim="form.otherText" type="textarea" :rows="3" :maxlength="500" show-word-limit placeholder="请具体说明您需要的服务" />
              </el-form-item>
            </section>

            <div class="form-actions">
              <p>提交成功后会显示提交回执。服务申请结果以工作人员回电答复为准。</p>
              <el-button type="primary" native-type="submit" :loading="submitting" class="submit-button">
                {{ submitting ? '正在提交' : '提交申请' }}
              </el-button>
            </div>
          </el-form>
        </template>

        <section v-else ref="receipt" class="success-section" aria-live="polite" aria-labelledby="receipt-title" tabindex="-1">
          <i class="el-icon-circle-check success-icon" aria-hidden="true"></i>
          <h1 id="receipt-title">申请提交成功</h1>
          <p class="receipt-message">您的申请已提交，我司会在24小时内回电答复您服务申请结果，请确保预留手机畅通。</p>
          <dl class="receipt-details">
            <div><dt>乘机人</dt><dd>{{ receipt.passengerName }}</dd></div>
            <div><dt>联系电话</dt><dd>{{ receipt.mobile }}</dd></div>
            <div><dt>航班</dt><dd>{{ receipt.flightNumber }} · {{ receipt.flightDate }}</dd></div>
            <div><dt>航段</dt><dd>{{ receipt.origin }}—{{ receipt.destination }}</dd></div>
            <div><dt>所需服务</dt><dd>{{ receipt.serviceName }}</dd></div>
            <div v-if="receipt.otherText"><dt>需求说明</dt><dd>{{ receipt.otherText }}</dd></div>
          </dl>
          <div class="receipt-actions">
            <el-button type="primary" plain class="submit-button return-entry-button" @click="goHome">返回首页</el-button>
            <el-button type="primary" class="submit-button" @click="continueApplication">继续申请</el-button>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script>
import ServiceLeft from '@/components/ServiceLeft';
import AirportPannel from '@/components/AirportPannel';
import { fetchAirports } from '@/api/newsapi';
import { submitSpecialServiceApplication } from '@/api/special-service';
import { DISABILITY_OPTIONS, SERVICE_OPTIONS } from './special-service-options';

function businessDate(time) {
  return new Date(time + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function createEmptyForm() {
  return {
    passengerName: '', mobile: '', ticketNumber: '', flightNumber: '', flightDate: '',
    origin: '', originCode: '', destination: '', destinationCode: '',
    disability: '', service: '', otherText: ''
  };
}

export default {
  name: 'SpecialServiceApplication',
  components: { ServiceLeft, AirportPannel },
  data() {
    const validateAirport = field => (rule, value, callback) => {
      if (!value || !this.form[field + 'Code'].trim()) {
        callback(new Error('请选择' + (field === 'origin' ? '出发机场' : '到达机场')));
      } else {
        callback();
      }
    };
    return {
      form: createEmptyForm(),
      rules: {
        passengerName: [
          { required: true, whitespace: true, message: '请填写乘机人姓名', trigger: 'blur' }
        ],
        mobile: [
          { required: true, whitespace: true, message: '请填写联系电话', trigger: 'blur' }
        ],
        ticketNumber: [
          { required: true, whitespace: true, message: '请填写票号', trigger: 'blur' }
        ],
        flightNumber: [
          { required: true, whitespace: true, message: '请填写航班号', trigger: 'blur' }
        ],
        flightDate: [{ required: true, message: '请选择航班日期', trigger: 'change' }],
        origin: [{ required: true, validator: validateAirport('origin'), trigger: 'blur' }],
        destination: [{ required: true, validator: validateAirport('destination'), trigger: 'blur' }],
        disability: [{ required: true, whitespace: true, message: '请选择一种残疾类型', trigger: 'change' }],
        service: [{ required: true, whitespace: true, message: '请选择一项特殊服务', trigger: 'change' }],
        otherText: [
          { required: true, whitespace: true, message: '请填写其他服务需求', trigger: 'blur' }
        ]
      },
      datePickerOptions: {
        disabledDate(date) {
          const now = Date.now();
          const minDate = businessDate(now);
          const maxDate = businessDate(now + 365 * 86400000);
          // 日历单元格使用设备本地日期，范围边界按上海时区计算。
          const selectedDate = [date.getFullYear(), ('0' + (date.getMonth() + 1)).slice(-2), ('0' + date.getDate()).slice(-2)].join('-');
          return selectedDate < minDate || selectedDate > maxDate;
        }
      },
      disabilityTypes: DISABILITY_OPTIONS,
      serviceTypes: SERVICE_OPTIONS,
      submitting: false,
      receipt: null
    };
  },
  computed: {
    requiresOtherDescription() {
      return this.form.service === 'OTHER';
    }
  },
  watch: {
    requiresOtherDescription(required) {
      if (!required) this.form.otherText = '';
      this.$nextTick(() => {
        if (this.$refs.applicationForm) this.$refs.applicationForm.clearValidate('otherText');
      });
    }
  },
  mounted() {
    document.title = this.$route.meta.title;
    window.scrollTo(0, 0);
    document.addEventListener('click', this.closeAirportPanels);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeAirportPanels);
  },
  methods: {
    closeAirportPanels() {
      ['originPanel', 'destinationPanel'].forEach(ref => {
        if (this.$refs[ref]) this.$refs[ref].close();
      });
    },
    openAirportPanel(field) {
      this.closeAirportPanels();
      if (!this.form[field] && !this.submitting) this.$refs[field + 'Panel'].show();
    },
    searchAirports(field, query, callback) {
      this.closeAirportPanels();
      const keyword = query.trim().toLowerCase();
      if (!keyword) {
        if (!this.submitting) this.$refs[field + 'Panel'].show();
        callback([]);
        return;
      }
      callback((fetchAirports() || []).filter(airport =>
        [airport.cityName, airport.chineseName, airport.airportCode, airport.pinYin]
          .some(value => (value || '').toLowerCase().indexOf(keyword) >= 0)
      ).map(airport => ({ ...airport, value: airport.cityName })));
    },
    onAirportInput(field) {
      this.form[field + 'Code'] = '';
      this.closeAirportPanels();
    },
    selectAirport(field, airport) {
      this.form[field] = airport.cityName;
      this.form[field + 'Code'] = airport.airportCode;
      this.closeAirportPanels();
      this.$refs.applicationForm.validateField(field);
      const otherField = field === 'origin' ? 'destination' : 'origin';
      if (this.form[otherField + 'Code']) this.$refs.applicationForm.validateField(otherField);
    },
    normalizeFlightNumber() {
      this.form.flightNumber = this.form.flightNumber.replace(/\s/g, '').toUpperCase();
    },
    focusFirstError() {
      this.$nextTick(() => {
        const field = this.$el.querySelector('.el-form-item.is-error');
        if (!field) return;
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = field.querySelector('[role="radio"], input, textarea');
        if (input) input.focus({ preventScroll: true });
      });
    },
    async submitApplication() {
      if (this.submitting || this.receipt) return;
      this.submitting = true;
      this.closeAirportPanels();
      this.normalizeFlightNumber();
      try {
        const valid = await new Promise(resolve => this.$refs.applicationForm.validate(resolve));
        if (!valid) {
          this.showSubmissionError('请检查标红的必填信息，再提交申请。');
          this.focusFirstError();
          return;
        }
        const application = { ...this.form, otherText: this.requiresOtherDescription ? this.form.otherText.trim() : '' };
        const response = await submitSpecialServiceApplication(application);
        if (!response || response.success !== true) {
          this.showSubmissionError(response && (response.errorMessage || response.message));
          return;
        }
        this.receipt = {
          passengerName: application.passengerName,
          mobile: application.mobile.trim().replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2'),
          flightNumber: application.flightNumber,
          flightDate: application.flightDate,
          origin: application.origin,
          destination: application.destination,
          serviceName: this.serviceTypes.find(item => item.value === application.service).label,
          otherText: application.otherText
        };
        this.$nextTick(() => {
          this.$refs.receipt.focus({ preventScroll: true });
          window.scrollTo(0, 0);
        });
      } catch (error) {
        const data = error.response && error.response.data;
        this.showSubmissionError(data && (data.errorMessage || data.message));
      } finally {
        this.submitting = false;
      }
    },
    showSubmissionError(message) {
      this.$message.error({
        message: message || '申请暂未提交成功，请稍后重试。',
        offset: 100
      });
    },
    continueApplication() {
      this.form = createEmptyForm();
      this.receipt = null;
      this.$nextTick(() => {
        this.$refs.applicationForm.clearValidate();
        window.scrollTo(0, 0);
      });
    },
    goHome() {
      this.$router.push('/');
      window.scrollTo(0, 0);
    }
  }
};
</script>

<style scoped lang="less">
.special-service-page {
  padding: 104px 0 64px;
  color: #333;
}
.service-layout { display: flex; align-items: flex-start; }
.service-sidebar {
  position: fixed;
  top: 104px;
  left: calc((100% - 1200px) / 2);
  width: 240px;
  /deep/ .search-block { width: 100%; }
}
@media (max-width: 1200px) {
  .service-sidebar { left: 0; }
}
.service-content {
  flex: 1;
  min-width: 0;
  margin-left: 264px;
}
.page-title { margin: 0 0 8px; font-size: 28px; font-weight: 600; }
.page-lead { margin-bottom: 20px; color: #68717e; line-height: 1.7; }
.time-notice {
  margin-bottom: 20px;
  padding: 16px 20px;
  background: #fff4f6;
  color: #d60039;
  border-left: 3px solid #d60039;
}
.form-section {
  margin-bottom: 16px;
  padding: 24px 28px 8px;
  background: #fff;
  border: 1px solid #edf0f2;
  border-radius: 6px;
}
.section-heading {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  margin-bottom: 20px;
  h2 { margin-right: 12px; font-size: 18px; font-weight: 600; }
  h2 span { margin-right: 10px; color: #d60039; }
  p { color: #909399; font-size: 13px; }
}
.airport-field { position: relative; }
.airport-code { float: right; margin-left: 16px; color: #909399; }
.ticket-notice {
  margin: 0 0 16px;
  padding: 12px 14px;
  background: #f6f7f9;
  color: #68717e;
  font-size: 12px;
  line-height: 1.7;
  i { margin-right: 6px; }
}
.choice-grid {
  display: flex;
  flex-wrap: wrap;
  margin: -6px;
  /deep/ .el-radio.is-bordered {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    height: auto;
    min-height: 48px;
    margin: 6px;
    padding: 12px;
  }
  /deep/ .el-radio.is-checked { background: #fff4f6; border-color: #d60039; }
  /deep/ .el-radio__label { white-space: normal; line-height: 1.6; }
}
.disability-choices /deep/ .el-radio { width: calc(25% - 12px); }
.service-choices /deep/ .el-radio { width: calc(50% - 12px); }
.form-actions {
  padding: 8px 0;
  text-align: center;
  p { margin-bottom: 14px; color: #68717e; font-size: 12px; line-height: 1.7; }
  .submit-button {
    width: 280px;
    max-width: 100%;
    &.is-loading, &.is-disabled {
      color: #fff;
      background: #d60039;
      border-color: #d60039;
    }
  }
}
.submit-button { min-width: 160px; min-height: 46px; border-radius: 4px; }
.success-section {
  padding: 40px 40px 36px;
  background: #fff;
  border: 1px solid #edf0f2;
  border-radius: 6px;
  text-align: center;
  h1 { margin: 14px 0; font-size: 28px; font-weight: 600; }
}
.success-icon { font-size: 58px; color: #22a06b; }
.receipt-message { margin: 0 0 28px; color: #68717e; font-size: 16px; line-height: 1.9; }
.receipt-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  .submit-button + .submit-button { margin-left: 16px; }
  .return-entry-button {
    color: #d60039;
    border-color: #d60039;
    background: transparent;
    transition: background-color 0.2s, border-color 0.2s, color 0.2s;
    &:hover, &:focus {
      color: #bc0032;
      border-color: #bc0032;
      background: #fff4f6;
    }
    &:active {
      color: #a9002d;
      border-color: #a9002d;
      background: #ffe8ee;
    }
  }
}
.receipt-details {
  margin-bottom: 30px;
  text-align: left;
  div { display: flex; padding: 15px 0; border-top: 1px solid #edf0f2; }
  dt { flex: 0 0 90px; color: #909399; }
  dd { word-break: break-word; white-space: pre-wrap; }
}
/deep/ .el-form-item__label { padding-bottom: 6px; color: #333; line-height: 24px; }
/deep/ .el-input__inner { height: 44px; line-height: 44px; border-radius: 4px; }
/deep/ .el-input__icon { line-height: 44px; }
/deep/ .el-date-editor.el-input, /deep/ .el-autocomplete { width: 100%; }
/deep/ .airport-field .filter-panel { top: 52px; box-sizing: border-box; max-width: 100%; }
/deep/ .airport-field .filter-city-list a { box-sizing: border-box; width: auto !important; min-width: 60px; }
</style>
